import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  TrendingDown,
  TrendingUp,
  Tags,
  PiggyBank,
  UserCircle2,
  LogOut,
  Menu,
  Sparkles,
  Repeat,
  Target,
  CreditCard,
  LineChart,
  Users,
  Shield,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import { Toaster } from "@/components/ui/sonner";
import InstallPrompt from "@/components/InstallPrompt";
import NotificationBell from "@/components/NotificationBell";
import { APP_VERSION_LABEL } from "@/config/version";

const BASE_NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/expenses", key: "expenses", icon: TrendingDown },
  { to: "/incomes", key: "income", icon: TrendingUp },
  { to: "/insights", key: "insights", icon: Sparkles },
  { to: "/recurring", key: "recurring", icon: Repeat },
  { to: "/goals", key: "goals", icon: Target },
  { to: "/debts", key: "debts", icon: CreditCard },
  { to: "/investments", key: "investments", icon: LineChart },
  { to: "/workspaces", key: "workspaces", icon: Users },
  { to: "/budget", key: "budget", icon: PiggyBank },
  { to: "/categories", key: "categories", icon: Tags },
  { to: "/profile", key: "profile", icon: UserCircle2 },
];

function NavItem({ to, label, icon: Icon, end, onClick, testKey }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={`nav-${testKey || label.toLowerCase()}`}
      className={({ isActive }) => `pnav-item ${isActive ? "is-active" : ""}`}
    >
      <Icon className="pnav-icon h-[18px] w-[18px]" strokeWidth={2} />
      <span>{label}</span>
    </NavLink>
  );
}

function IconButton({ as = "button", to, onClick, ariaLabel, testid, children, className = "" }) {
  const cls = `picon-btn ${className}`;
  if (as === "link") {
    return (
      <Link to={to} className={cls} aria-label={ariaLabel} data-testid={testid} title={ariaLabel}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls} aria-label={ariaLabel} data-testid={testid} title={ariaLabel}>
      {children}
    </button>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const NAV = user?.is_admin
    ? [...BASE_NAV.slice(0, -1), { to: "/admin", key: "admin", icon: Shield }, BASE_NAV[BASE_NAV.length - 1]]
    : BASE_NAV;

  const onLogout = () => {
    logout();
    navigate("/auth", { replace: true });
  };

  const initial = (user?.name || user?.email || "U").slice(0, 1).toUpperCase();
  const firstName = user?.name?.split(" ")[0] || user?.email?.split("@")[0] || "there";

  // Time-aware greeting
  const hr = new Date().getHours();
  const greeting =
    hr < 5 ? t("greet_welcome_back")
    : hr < 12 ? t("greet_morning")
    : hr < 17 ? t("greet_afternoon")
    : hr < 21 ? t("greet_evening")
    : t("greet_welcome_back");

  const sidebar = (close) => (
    <aside className="pside flex h-full w-[280px] flex-col">
      {/* Logo area */}
      <div className="px-6 pt-8 pb-6 overflow-visible">
        <Link to="/" onClick={close} className="flex items-center gap-3.5" data-testid="brand-link">
          <span className="relative inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl">
            <img
              src="/icons/brand-mark.png"
              alt="Expense & Save"
              className="h-12 w-12 rounded-2xl shadow-[0_10px_24px_-10px_rgba(30,63,51,0.55)] dark:shadow-[0_10px_28px_-8px_rgba(0,0,0,0.65)]"
              loading="eager"
              decoding="async"
            />
          </span>
          <div className="leading-tight">
            <div className="font-display text-[17px] font-extrabold tracking-tight">Expense&nbsp;&amp;&nbsp;Save</div>
            <div className="text-[11px] font-medium text-muted-foreground">{t("tagline")}</div>
          </div>
        </Link>
      </div>

      <div className="pside-divider mx-6" />

      {/* Nav */}
      <nav className="flex-1 space-y-1 px-4 py-4 overflow-y-auto" data-testid="primary-nav">
        {NAV.map((n) => (
          <NavItem key={n.to} to={n.to} end={n.end} icon={n.icon} label={t(n.key)} testKey={n.key} onClick={close} />
        ))}
      </nav>

      {/* User card + logout + footer */}
      <div className="border-t border-border/60 p-4">
        <div className="mb-2.5 flex items-center gap-3 rounded-2xl border border-border/70 bg-background/40 px-3 py-2.5">
          <div className="pavatar !h-9 !w-9 !text-[13px]">{initial}</div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-bold tracking-tight" data-testid="sidebar-user-name">{user?.name || "User"}</div>
            <div className="truncate text-[11px] text-muted-foreground">{user?.email}</div>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={onLogout}
          data-testid="logout-button"
          className="w-full justify-start gap-2 rounded-xl text-[13px] font-semibold text-muted-foreground hover:text-foreground"
        >
          <LogOut className="h-4 w-4" /> {t("logout")}
        </Button>
        <div
          className="mt-3 px-1 text-[10.5px] font-medium uppercase tracking-[0.18em] text-muted-foreground/70"
          data-testid="sidebar-version"
        >
          Expense &amp; Save · {APP_VERSION_LABEL}
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Skip-to-content link for keyboard / screen-reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-full focus:bg-primary focus:px-4 focus:py-2 focus:text-[12px] focus:font-bold focus:text-primary-foreground focus:shadow-[0_8px_18px_-6px_hsl(var(--primary)/0.55)]"
      >
        {t("skip_to_main")}
      </a>

      {/* Desktop sidebar */}
      <div className="hidden md:flex">{sidebar(() => {})}</div>

      {/* Mobile sidebar overlay */}
      {open && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="relative z-50 animate-in slide-in-from-left-8 duration-200">{sidebar(() => setOpen(false))}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top navigation */}
        <header className="phero-bar sticky top-0 z-30 flex h-[72px] items-center justify-between border-b border-border/70 px-4 md:px-8" data-testid="topbar">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setOpen(true)}
              aria-label={t("open_menu")}
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <button
              type="button"
              onClick={() => {
                if (location.pathname !== "/") navigate("/");
              }}
              aria-label={t("greet_welcome_back")}
              data-testid="topbar-greeting"
              className="flex flex-col items-start rounded-lg text-left transition duration-200 ease-out hover:scale-[1.015] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 active:scale-[0.985]"
            >
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-white/75">{greeting},</span>
              <span className="font-display text-[20px] font-extrabold leading-tight tracking-tight text-white md:text-[22px]">
                {firstName.toUpperCase()}
              </span>
            </button>
          </div>

          <div className="flex items-center gap-2.5">
            <ThemeToggle />
            <NotificationBell />
            <IconButton
              as="link"
              to="/profile?tab=details"
              ariaLabel={t("settings")}
              testid="topbar-settings"
            >
              <Settings className="h-[18px] w-[18px]" strokeWidth={2} />
            </IconButton>
            <Link
              to="/profile"
              className="pavatar"
              aria-label={t("profile")}
              data-testid="topbar-avatar"
              title={user?.name || user?.email}
            >
              {initial}
            </Link>
          </div>
        </header>

        <main
          id="main-content"
          key={location.pathname}
          className="ppage-in mx-auto w-full min-w-0 max-w-7xl flex-1 overflow-x-clip px-4 py-6 md:px-8 md:py-10"
        >
          <Outlet />
        </main>
      </div>

      <Toaster position="top-right" richColors closeButton />
      <InstallPrompt />
    </div>
  );
}
